import { motion, useReducedMotion } from "framer-motion";
import { ArrowLeft, Instagram, Plus } from "lucide-react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import cn from "~/utils/cn";
import Button from "../../ui/Button";
import useDesign from "../../useDesign";
import { INSTAGRAM_URL } from "../types";
import SectionTitle from "./SectionTitle";

type QA = { q: string; a: string };

// FAQ section (id="faq"). "accordion" for a (beside the contact card) and c (one centred column of story cards),
// "editorial" (every answer visible, hairline rows) for b.
export default function Faq({ variant, defaultOpen = 0, className }: { variant: "accordion" | "editorial"; defaultOpen?: number | null; className?: string }) {
  const { t } = useTranslation();
  const items = t("ui.landing.faq.items", { returnObjects: true }) as QA[];
  const uid = useId();
  const titleId = `${uid}-faq`;

  if (variant === "editorial")
    return (
      <section id="faq" aria-labelledby={titleId} className={cn("mx-auto w-full max-w-[1200px] scroll-mt-[var(--nav-h)] px-[var(--gutter)]", className)}>
        <SectionTitle id={titleId} title={t("ui.landing.faq.title")} className="mb-7 md:mb-12" />
        <dl>
          {items.map((item) => (
            <div key={item.q} className="grid gap-1.5 border-t border-line py-6 md:grid-cols-[5fr_7fr] md:gap-14 md:py-9">
              <dt className="text-balance font-display text-lg font-bold leading-[1.6] text-ink md:text-xl">{item.q}</dt>
              <dd className="max-w-[58ch] leading-[1.9] text-ink-2">{item.a}</dd>
            </div>
          ))}
          <div className="grid gap-1.5 border-y border-line py-6 md:grid-cols-[5fr_7fr] md:gap-14 md:py-9">
            <dt className="font-display text-lg font-bold leading-[1.6] text-ink md:text-xl">{t("ui.landing.faq.more")}</dt>
            <dd className="leading-[1.9] text-ink-2">
              <p>{t("ui.landing.faq.moreBody")}</p>
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group mt-1 inline-flex min-h-11 items-center gap-2 font-display font-bold text-accent-text underline decoration-[color-mix(in_srgb,currentColor_35%,transparent)] decoration-1 underline-offset-[6px] hover:decoration-current"
              >
                <Instagram className="size-5" strokeWidth={1.75} aria-hidden />
                {t("ui.landing.faq.instagram")}
                <ArrowLeft className="size-4 transition-transform duration-300 ease-out group-hover:-translate-x-1 ltr:rotate-180" strokeWidth={1.75} aria-hidden />
              </a>
            </dd>
          </div>
        </dl>
      </section>
    );

  return <Accordion items={items} titleId={titleId} uid={uid} defaultOpen={defaultOpen} className={className} />;
}

function Accordion({ items, titleId, uid, defaultOpen, className }: { items: QA[]; titleId: string; uid: string; defaultOpen: number | null; className?: string }) {
  const { t } = useTranslation();
  const design = useDesign();
  const reduce = useReducedMotion();
  const [open, setOpen] = useState<number | null>(defaultOpen);
  const [toggled, setToggled] = useState(false); // the server-rendered open answer shows as is; later opens fade in
  const c = design === "c";

  return (
    <section
      id="faq"
      aria-labelledby={titleId}
      className={cn(
        "mx-auto grid w-full scroll-mt-[var(--nav-h)] gap-7 px-[var(--gutter)]",
        c ? "max-w-[880px] md:gap-8" : "max-w-[1200px] md:grid-cols-[minmax(0,.8fr)_minmax(0,1.2fr)] md:items-start md:gap-x-16 md:gap-y-8",
        className,
      )}
    >
      <SectionTitle id={titleId} title={t("ui.landing.faq.title")} className={c ? undefined : "md:col-start-1 md:row-start-1"} />

      <div className={cn(c ? "flex flex-col gap-3" : "divide-y divide-line rounded-lg border border-line bg-surface shadow-card md:col-start-2 md:row-span-2 md:row-start-1")}>
        {items.map((item, i) => {
          const isOpen = open === i;
          const q = `${uid}-q${i}`;
          const a = `${uid}-a${i}`;
          return (
            <div key={item.q} className={cn(c && "rounded-lg bg-surface ring-1 ring-inset ring-line")}>
              <h3>
                <button
                  type="button"
                  id={q}
                  aria-expanded={isOpen}
                  aria-controls={a}
                  onClick={() => (setOpen(isOpen ? null : i), setToggled(true))}
                  className="flex min-h-11 w-full items-center justify-between gap-4 rounded-lg px-5 py-5 text-start font-display text-[17px] font-bold leading-[1.6] text-ink focus-visible:outline-offset-[-3px] md:px-7 md:text-lg"
                >
                  <span>{item.q}</span>
                  <span
                    aria-hidden
                    className={cn(
                      "grid size-10 shrink-0 place-items-center rounded-full transition-transform duration-500 ease-spring",
                      c ? "bg-accent-soft text-ink" : "bg-accent-soft text-accent-text",
                      isOpen && "rotate-45",
                    )}
                  >
                    <Plus className="size-5" strokeWidth={1.75} />
                  </span>
                </button>
              </h3>
              {/* the answer toggles at once and only its text fades and rises: no height animation, so no per-frame layout */}
              <div id={a} role="region" aria-labelledby={q} hidden={!isOpen}>
                {isOpen && (
                  <motion.p
                    initial={toggled ? { opacity: 0, y: -6 } : false}
                    animate={{ opacity: 1, y: 0 }}
                    transition={reduce ? { duration: 0 } : { duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                    className="max-w-[62ch] px-5 pb-6 leading-[1.9] text-ink-2 md:px-7"
                  >
                    {item.a}
                  </motion.p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div
        className={cn(
          "flex flex-wrap items-center gap-x-5 gap-y-4 rounded-lg p-6",
          c ? "shadow-card ring-1 ring-inset ring-line" : "border border-line bg-surface shadow-card md:col-start-1 md:row-start-2 md:max-w-[420px]",
        )}
        style={c ? { background: "linear-gradient(160deg,#356bce,#204ca5)" } : undefined}
      >
        <div className="min-w-0 flex-[1_1_220px]">
          <h3 className="font-display text-lg font-extrabold leading-[1.6] text-ink">{t("ui.landing.faq.more")}</h3>
          <p className={cn("mt-1 text-[15px] leading-[1.8]", c ? "text-ink" : "text-ink-2")}>{t("ui.landing.faq.moreBody")}</p>
        </div>
        <Button href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" variant={c ? "primary" : "secondary"} size="md" className="h-12 px-5">
          <Instagram className="size-5" strokeWidth={1.75} aria-hidden />
          {t("ui.landing.faq.instagram")}
        </Button>
      </div>
    </section>
  );
}
